import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'

import { mergeReducedMotionPreset, useThemeMotion } from '../../../animation/themeMotion'
import type { ComicPanelAspectPreset } from '../../../lib/comicProjectSettings'
import type { ComicReaderPageEntry } from '../../../lib/comicReaderModel'
import { readerProgressLabel } from '../../../lib/comicReaderModel'
import type { ComicEpisode } from '../../../lib/comicTypes'
import { ChromeToolbarButton } from '../../ChromeActions'
import { IconChevronRight } from '../../nav-icons'
import { ComicReaderPageContent } from './ComicReaderPageContent'
import { ComicReaderPageStrip } from './ComicReaderPageStrip'

type Props = {
  angular: boolean
  entries: ComicReaderPageEntry[]
  episodesById: Map<string, ComicEpisode>
  panelAspect: ComicPanelAspectPreset
  pageIndex: number
  onPageIndexChange: (index: number) => void
  onNavigatePage?: (episodeId: string, pageId: string, panelId?: string) => void
}

const SWIPE_THRESHOLD_PX = 48

export function ComicReaderPaginateView({
  angular,
  entries,
  episodesById,
  panelAspect,
  pageIndex,
  onPageIndexChange,
  onNavigatePage,
}: Props) {
  const prefersReduced = useReducedMotion()
  const motionPreset = useThemeMotion()
  const preset = mergeReducedMotionPreset(motionPreset, !!prefersReduced)
  const touchStartX = useRef<number | null>(null)
  const [direction, setDirection] = useState<1 | -1>(1)

  const total = entries.length
  const clampedIndex = total > 0 ? Math.min(Math.max(0, pageIndex), total - 1) : 0
  const entry = entries[clampedIndex] ?? null
  const episode = entry ? episodesById.get(entry.episodeId) : null

  const goPrev = useCallback(() => {
    if (clampedIndex <= 0) return
    setDirection(-1)
    onPageIndexChange(clampedIndex - 1)
  }, [clampedIndex, onPageIndexChange])

  const goNext = useCallback(() => {
    if (clampedIndex >= total - 1) return
    setDirection(1)
    onPageIndexChange(clampedIndex + 1)
  }, [clampedIndex, onPageIndexChange, total])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [goPrev, goNext])

  if (!entry || !episode) {
    return (
      <p className="typo-lib-empty flex flex-1 items-center justify-center py-16 text-center text-ink-soft">
        没有可翻页的页面
      </p>
    )
  }

  const slideVariants = prefersReduced
    ? { enter: { opacity: 0 }, center: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        enter: (d: number) => ({ opacity: 0, x: d > 0 ? 28 : -28 }),
        center: { opacity: 1, x: 0 },
        exit: (d: number) => ({ opacity: 0, x: d > 0 ? -28 : 28 }),
      }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line bg-panel/90 px-3 py-2 sm:px-5">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{entry.episodeTitle}</p>
          <p className="truncate text-xs text-ink-soft">{entry.page.title}</p>
        </div>
        <span className="shrink-0 text-xs font-semibold tabular-nums text-ink-soft">
          {readerProgressLabel(clampedIndex, total)}
        </span>
      </div>
      <div
        className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden bg-[color-mix(in_srgb,var(--surface)_88%,#000_12%)] px-2 py-2 sm:px-4 sm:py-3"
        onTouchStart={(e) => {
          touchStartX.current = e.changedTouches[0]?.clientX ?? null
        }}
        onTouchEnd={(e) => {
          const start = touchStartX.current
          touchStartX.current = null
          if (start == null) return
          const end = e.changedTouches[0]?.clientX ?? start
          const delta = end - start
          if (delta > SWIPE_THRESHOLD_PX) goPrev()
          else if (delta < -SWIPE_THRESHOLD_PX) goNext()
        }}
      >
        <button
          type="button"
          className="absolute inset-y-0 left-0 z-[2] w-[28%] max-w-[9rem] border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40 enabled:cursor-pointer disabled:cursor-default"
          aria-label="上一页"
          disabled={clampedIndex <= 0}
          onClick={goPrev}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 z-[2] w-[28%] max-w-[9rem] border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40 enabled:cursor-pointer disabled:cursor-default"
          aria-label="下一页"
          disabled={clampedIndex >= total - 1}
          onClick={goNext}
        />

        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={entry.key}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: Math.min(0.28, preset.duration), ease: preset.easing }}
            className="pointer-events-auto relative z-[1] flex h-full min-h-0 w-full max-w-3xl items-center justify-center px-1"
          >
            <ComicReaderPageContent
              angular={angular}
              episode={episode}
              page={entry.page}
              panelAspect={panelAspect}
              paginate
              onSelectPage={
                onNavigatePage
                  ? () => onNavigatePage(entry.episodeId, entry.page.id)
                  : undefined
              }
              onSelectPanel={
                onNavigatePage
                  ? (panelId) => onNavigatePage(entry.episodeId, entry.page.id, panelId)
                  : undefined
              }
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <footer className="flex shrink-0 flex-col gap-2 border-t border-line bg-panel/95 px-3 py-2.5 sm:px-5">
        <ComicReaderPageStrip
          angular={angular}
          entries={entries}
          episodesById={episodesById}
          pageIndex={clampedIndex}
          onSelectIndex={(idx) => {
            if (idx !== clampedIndex) setDirection(idx > clampedIndex ? 1 : -1)
            onPageIndexChange(idx)
          }}
        />
        <div className="flex w-full items-center justify-between gap-3">
          <ChromeToolbarButton
            type="button"
            className="inline-flex items-center gap-1 px-2"
            disabled={clampedIndex <= 0}
            onClick={goPrev}
          >
            <IconChevronRight className="h-4 w-4 rotate-180" aria-hidden />
            上一页
          </ChromeToolbarButton>
          <ChromeToolbarButton
            type="button"
            className="inline-flex items-center gap-1 px-2"
            disabled={clampedIndex >= total - 1}
            onClick={goNext}
          >
            下一页
            <IconChevronRight className="h-4 w-4" aria-hidden />
          </ChromeToolbarButton>
        </div>
      </footer>
    </div>
  )
}
